import { PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { SidebarContent, SidebarProvider } from '@/components/ui/sidebar'
import { cn } from '@/lib/utils'
import { AccountContent } from './AccountContent'
import { DEFAULT_ORG_NAME, Viewport } from './accountModel'
import { ChatPane } from './ChatPane'
import { Message, ONBOARDING_THREAD } from './contacts'
import { FleetTopNav } from './FleetTopNav'
import { NavRows } from './NavRows'
import { BlankStateDetail, ProductBlankState } from './ProductBlankState'
import { navGroups, ProductTab, PROTOTYPE_TABS, TAB_ENTITY } from './prototypeNav'
import { TeamPanel } from './TeamPanel'

/** Room to read in, the way the agent's own page gives it elsewhere. */
const AGENT_CHAT_WIDTH = 520

/** The chat is the page on the agent's tab, and an edge of it on a product's. */
const CHAT_RAIL = 300

/** A margin note beside the agent, not a column competing with it. */
const TEAM_PANEL_WIDTH = 220

/** Until the row is measured. The animation needs a number to travel between,
 *  so the wide state is whatever the row actually is rather than a guess. */
const CHAT_WIDE_FALLBACK = 1100

/**
 * Lighter than ink-50, which is the lightest the ramp goes: a one-off so the
 * chat barely separates from the page beside it. The bars stay white.
 */
const CHROME_TINT = 'bg-[#f9f9f8]'

/** What the composer says where the agent cannot reach the product behind the tab. */
const FUEL_COMPOSER = 'Cai is not fully integrated with FuelApp'

/** Leaving the agent is a quick exit; coming back to it is an arrival. */
const EASING = 'ease-out motion-reduce:transition-none'

// The thread only rewraps while the column is narrower than the width it is
// capped at; ease-out clears that early, so the fade is short.
const REVEAL_AWAY_MS = 150

/**
 * Day one. The account exists and the agent is talking, but nothing has been
 * bought yet — so the products are tabs with an offer behind them rather than
 * screens, and there is no rail to collapse.
 */
export function ProductOnboardingDesktop({
  className,
  tabs = PROTOTYPE_TABS,
  thread = ONBOARDING_THREAD,
  agentTabLabel,
  tabLabels,
  handoff,
  tabDetail,
  tabPages,
  composerFooter,
  softComposer,
  chromeTint,
  activeTabs,
  profile,
}: {
  className?: string
  /** Which products the account carries, for comparing one line-up against another. */
  tabs?: ProductTab[]
  /** What the agent is saying, which changes with what the agent is being sold as. */
  thread?: Message[]
  /** Names the agent's tab, where the agent has absorbed a product. */
  agentTabLabel?: string
  /** Renames products for this mock only. */
  tabLabels?: Partial<Record<ProductTab, string>>
  /** The chat docks instead of vanishing, so the avatar travels between the two. */
  handoff?: boolean
  /** Extra content for a product's offer, where one has more to say than a price. */
  tabDetail?: Partial<Record<ProductTab, BlankStateDetail>>
  /** A product that is past being an offer, and has a page of its own. */
  tabPages?: Partial<Record<ProductTab, React.ReactNode>>
  /** Sits under the composer — trial state, and the like. */
  composerFooter?: React.ReactNode
  /** Products the fleet has bought: these get their rail and pages, not an offer. */
  activeTabs?: ProductTab[]
  /** Replaces the plain avatar in the bar. */
  profile?: React.ReactNode
  /** The setup flow's composer: a recessed surface, rounder, green to send. */
  softComposer?: boolean
  /** The chat sits on a tint, between two white bars. */
  chromeTint?: boolean
}) {
  const [tab, setTab] = useState<ProductTab>(tabs[0])
  const [onAgent, setOnAgent] = useState(true)
  const [bodyFaded, setBodyFaded] = useState(false)
  const reveal = useRef<number | undefined>(undefined)
  const row = useRef<HTMLDivElement>(null)
  const [wide, setWide] = useState(CHAT_WIDE_FALLBACK)

  useEffect(() => {
    const node = row.current
    if (!node) return
    const resize = new ResizeObserver(([entry]) => setWide(entry.contentRect.width))
    resize.observe(node)
    return () => resize.disconnect()
  }, [])
  const glide = cn(EASING, onAgent ? 'duration-700' : 'duration-300')

  useEffect(() => () => window.clearTimeout(reveal.current), [])

  // A bought product opens on its first screen; an offer has none to open on.
  const [page, setPage] = useState<string | null>(null)
  // Collapsed by default: under this placement the chat is the column that
  // earns the room, which is what the other desktops settled on too.
  const [navOpen, setNavOpen] = useState(false)
  // Bumped on the way out of the agent's page, to reopen the docked pane on
  // the conversation rather than whichever surface was last looked at.
  const [showChat, setShowChat] = useState(0)

  function openTab(next: ProductTab) {
    setTab(next)
    setPage(navGroups(next)[0]?.rows[0]?.title ?? null)
  }

  function goTo(next: boolean) {
    if (next === onAgent) return
    setOnAgent(next)
    window.clearTimeout(reveal.current)
    // Only the outbound trip hides the thread; arriving back, the fade read
    // worse than the reflow it was covering.
    if (next) {
      setBodyFaded(false)
      return
    }
    setBodyFaded(true)
    reveal.current = window.setTimeout(() => setBodyFaded(false), REVEAL_AWAY_MS)
  }

  // FuelApp is a product Cai has not been wired into yet.
  const fuelTab = !onAgent && tab === ProductTab.FUEL

  const bought = activeTabs?.includes(tab)
  const rows = bought ? navGroups(tab) : []

  const productPage = bought ? (
    <AccountContent viewport={Viewport.DESKTOP} page={page} />
  ) : (
    (tabPages?.[tab] ?? <ProductBlankState tab={tab} label={tabLabels?.[tab]} detail={tabDetail?.[tab]} />)
  )

  return (
    <SidebarProvider
      open={navOpen}
      onOpenChange={setNavOpen}
      className={cn('border-ink-100 block min-h-0 overflow-hidden border bg-white', className)}
    >
      <div className="flex h-full flex-col">
        <FleetTopNav
          tab={tab}
          onSelect={(next) => {
            openTab(next)
            if (onAgent) setShowChat((count) => count + 1)
            if (handoff) goTo(false)
            else setOnAgent(false)
          }}
          orgName={DEFAULT_ORG_NAME}
          tabs={tabs}
          agentTabLabel={agentTabLabel}
          tabLabels={tabLabels}
          profile={profile}
          symbolOnlyLogo
          agentActive={onAgent}
          onAgent={() => (handoff ? goTo(true) : setOnAgent(true))}
        />

        <div ref={row} className="flex min-h-0 flex-1">
          {handoff ? (
            <>
              {/* Everything animates off the chat's width: the avatar travels
                because the column it is centred in narrows and slides right. */}
              {/* The rail this prototype settled on: icons by default, labels
                  and the entity behind a toggle, and no dashboard above them. */}
              {rows.length > 0 && !onAgent && (
                <div
                  className={cn(
                    'border-ink-100 flex shrink-0 flex-col border-r',
                    navOpen ? 'w-40' : 'w-(--sidebar-width-icon)'
                  )}
                >
                  <SidebarContent className="no-scrollbar gap-0">
                    <NavRows tab={tab} page={page} onNavigate={setPage} collapsed={!navOpen} />
                    {navOpen && (
                      <p className="text-ink-300 mt-auto truncate px-4 py-3 text-[10px] leading-4">
                        {TAB_ENTITY[tab]}
                      </p>
                    )}
                  </SidebarContent>
                  <button
                    type="button"
                    onClick={() => setNavOpen((was) => !was)}
                    aria-label={navOpen ? 'Collapse navigation' : 'Expand navigation'}
                    aria-expanded={navOpen}
                    className="hover:bg-sidebar-accent mt-auto flex items-center justify-center py-2"
                  >
                    {navOpen ? (
                      <PanelLeftClose className="text-ink-300 size-4" />
                    ) : (
                      <PanelLeftOpen className="text-ink-300 size-4" />
                    )}
                  </button>
                </div>
              )}
              <div
                className={cn(
                  'min-w-0 flex-1 overflow-hidden transition-opacity',
                  glide,
                  onAgent && 'opacity-0'
                )}
              >
                {productPage}
              </div>
              <ChatPane
                width={onAgent ? wide - TEAM_PANEL_WIDTH : CHAT_RAIL}
                contentWidth={AGENT_CHAT_WIDTH}
                className={cn(
                  'border-l transition-[width,border-color]',
                  glide,
                  onAgent ? 'border-transparent' : 'border-ink-100',
                  chromeTint && CHROME_TINT
                )}
                hideAgentLabel
                connect
                agentThread={thread}
                bodyFaded={bodyFaded}
                showChat={showChat}
                composerFooter={composerFooter}
                softComposer={softComposer}
                disabled={fuelTab}
                composerLabel={fuelTab ? FUEL_COMPOSER : undefined}
              />
              <div
                className={cn('flex shrink-0 overflow-hidden transition-[width]', glide)}
                style={{ width: onAgent ? TEAM_PANEL_WIDTH : 0 }}
              >
                <TeamPanel width={TEAM_PANEL_WIDTH} />
              </div>
            </>
          ) : onAgent ? (
            <ChatPane
              width="fill"
              contentWidth={AGENT_CHAT_WIDTH}
              hideAgentLabel
              connect
              agentThread={thread}
              composerFooter={composerFooter}
              softComposer={softComposer}
              className={cn(chromeTint && CHROME_TINT)}
            />
          ) : (
            productPage
          )}
        </div>
      </div>
    </SidebarProvider>
  )
}
